import React, { useState, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { v4 as uuidv4 } from 'uuid';
import { toast } from '../shared/Toast';

const goalIcons = ['fa-car','fa-plane','fa-house','fa-graduation-cap','fa-heart','fa-laptop','fa-bicycle','fa-star','fa-piggy-bank','fa-umbrella'];
const goalColors = { 'fa-car':'#FAEEDA', 'fa-plane':'#E1F5EE', 'fa-house':'#E6F1FB', 'fa-graduation-cap':'#EAF3DE', default:'#F1EFE8' };
const goalIconColors = { 'fa-car':'#854F0B', 'fa-plane':'#0F6E56', 'fa-house':'#185FA5', 'fa-graduation-cap':'#3B6D11', default:'#888780' };
const barColors = { 'fa-car':'var(--gold)', 'fa-plane':'#1d9e75', 'fa-house':'#378ADD', 'fa-graduation-cap':'#3B6D11', default:'var(--gold)' };

function GoalModal({ goal, onClose }) {
  const { dispatch } = useApp();
  const [form, setForm] = useState(goal || { name: '', description: '', targetValue: '', currentValue: 0, startDate: new Date().toISOString().slice(0,10), targetDate: '', icon: 'fa-piggy-bank', status: 'active' });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = () => {
    if (!form.name.trim()) { toast.error('Nome da meta é obrigatório.'); return; }
    if (!form.targetValue || parseFloat(form.targetValue) <= 0) { toast.error('Valor alvo deve ser maior que zero.'); return; }
    const payload = { ...form, targetValue: parseFloat(form.targetValue), currentValue: parseFloat(form.currentValue) || 0 };
    if (goal) dispatch({ type: 'UPDATE_GOAL', payload });
    else dispatch({ type: 'ADD_GOAL', payload: { id: uuidv4(), ...payload } });
    onClose();
  };
  return (
    <div className="modal-backdrop">
      <div className="modal">
        <div className="modal-handle"></div>
        <div className="modal-title">{goal ? 'Editar meta' : 'Nova meta'}<button className="close-btn" onClick={onClose}>×</button></div>
        <div className="form-group"><label className="form-label">Nome da meta</label><input className="form-control" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Ex: Viagem Europa" /></div>
        <div className="form-row">
          <div className="form-group"><label className="form-label">Valor alvo (R$)</label><input className="form-control" type="number" value={form.targetValue} onChange={e => set('targetValue', e.target.value)} /></div>
          <div className="form-group"><label className="form-label">Valor atual (R$)</label><input className="form-control" type="number" value={form.currentValue} onChange={e => set('currentValue', e.target.value)} /></div>
        </div>
        <div className="form-row">
          <div className="form-group"><label className="form-label">Data início</label><input className="form-control" type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)} /></div>
          <div className="form-group"><label className="form-label">Data alvo</label><input className="form-control" type="date" value={form.targetDate} onChange={e => set('targetDate', e.target.value)} /></div>
        </div>
        <div className="form-group">
          <label className="form-label">Ícone</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {goalIcons.map(ic => (
              <div key={ic} onClick={() => set('icon', ic)} style={{ width: 34, height: 34, borderRadius: '50%', background: form.icon === ic ? '#FAEEDA' : 'var(--surface2)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: form.icon === ic ? '2px solid var(--gold)' : '1px solid var(--border)' }}>
                <i className={`fas ${ic}`} style={{ fontSize: 14, color: form.icon === ic ? '#854F0B' : 'var(--muted)' }}></i>
              </div>
            ))}
          </div>
        </div>
        <button className="btn btn-primary btn-full" onClick={save}><i className="fas fa-check"></i> Salvar meta</button>
      </div>
    </div>
  );
}

function DepositModal({ goal, onClose }) {
  const { state, dispatch } = useApp();
  const [amount, setAmount] = useState('');
  const deposit = () => {
    const val = parseFloat(amount);
    if (!val || val <= 0) return;
    const updated = { ...goal, currentValue: Math.min(goal.currentValue + val, goal.targetValue) };
    if (updated.currentValue >= updated.targetValue) updated.status = 'completed';
    dispatch({ type: 'UPDATE_GOAL', payload: updated });
    onClose();
  };
  return (
    <div className="modal-backdrop">
      <div className="modal">
        <div className="modal-handle"></div>
        <div className="modal-title">Depositar na meta<button className="close-btn" onClick={onClose}>×</button></div>
        <div style={{ marginBottom: 14, color: 'var(--muted)', fontSize: 13 }}>Meta: <strong style={{ color: 'var(--text)' }}>{goal.name}</strong></div>
        <div style={{ marginBottom: 14, color: 'var(--muted)', fontSize: 13 }}>
          Faltam: <strong style={{ color: 'var(--red)' }}>{formatCurrency(goal.targetValue - goal.currentValue)}</strong>
        </div>
        <div className="form-group"><label className="form-label">Valor do depósito (R$)</label><input className="form-control" type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0,00" autoFocus /></div>
        <button className="btn btn-primary btn-full" onClick={deposit}><i className="fas fa-plus"></i> Depositar</button>
      </div>
    </div>
  );
}

export default function Goals() {
  const { state, dispatch } = useApp();
  const [modal, setModal] = useState(null);
  const [editGoal, setEditGoal] = useState(null);
  const [depositGoal, setDepositGoal] = useState(null);

  const cats = useMemo(() => Object.fromEntries(state.categories.map(c => [c.id, c])), [state.categories]);
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  const budgetSpent = useMemo(() => {
    const map = {};
    state.transactions.filter(t => t.date.startsWith(currentMonth) && t.type === 'expense' && t.status === 'paid').forEach(t => {
      map[t.categoryId] = (map[t.categoryId] || 0) + t.value;
    });
    return map;
  }, [state.transactions, currentMonth]);

  const activeGoals = state.goals.filter(g => g.status === 'active');
  const completedGoals = state.goals.filter(g => g.status === 'completed');

  return (
    <div>
      <div className="row" style={{ marginBottom: 14 }}>
        <span style={{ fontSize: 17, fontWeight: 600 }}>Metas e Orçamento</span>
        <button className="btn btn-primary btn-sm" onClick={() => { setEditGoal(null); setModal('goal'); }}>
          <i className="fas fa-plus"></i> Nova meta
        </button>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="section-title">Metas ativas</div>
        {activeGoals.length === 0 && <div className="empty-state"><i className="fas fa-bullseye"></i><div>Nenhuma meta ativa</div></div>}
        {activeGoals.map(goal => {
          const pct = goal.targetValue > 0 ? Math.min(100, Math.round(goal.currentValue / goal.targetValue * 100)) : 0;
          const bg = goalColors[goal.icon] || goalColors.default;
          const ic = goalIconColors[goal.icon] || goalIconColors.default;
          const bar = barColors[goal.icon] || barColors.default;
          const remaining = goal.targetValue - goal.currentValue;
          return (
            <div className="goal-item" key={goal.id}>
              <div className="goal-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div className="goal-icon" style={{ background: bg }}>
                    <i className={`fas ${goal.icon}`} style={{ color: ic }}></i>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{goal.name}</div>
                    {goal.targetDate && <div style={{ fontSize: 11, color: 'var(--muted)' }}>Até {formatDate(goal.targetDate + 'T12:00:00')}</div>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--gold)' }}>{pct}%</span>
                  <button onClick={() => { setDepositGoal(goal); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--green)', fontSize: 16, padding: 2 }} title="Depositar"><i className="fas fa-plus-circle"></i></button>
                  <button onClick={() => { setEditGoal(goal); setModal('goal'); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 14, padding: 2 }} title="Editar"><i className="fas fa-pen"></i></button>
                  <button onClick={() => { if (window.confirm('Excluir esta meta?')) dispatch({ type: 'DELETE_GOAL', payload: goal.id }); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)', fontSize: 14, padding: 2 }} title="Excluir"><i className="fas fa-trash"></i></button>
                </div>
              </div>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${pct}%`, background: bar }}></div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                <span>{formatCurrency(goal.currentValue)} / {formatCurrency(goal.targetValue)}</span>
                <span>Faltam {formatCurrency(remaining)}</span>
              </div>
            </div>
          );
        })}
      </div>

      {completedGoals.length > 0 && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div className="section-title">Metas concluídas 🎉</div>
          {completedGoals.map(goal => (
            <div className="goal-item" key={goal.id}>
              <div className="goal-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div className="goal-icon" style={{ background: '#EAF3DE' }}>
                    <i className={`fas ${goal.icon}`} style={{ color: '#3B6D11' }}></i>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{goal.name}</div>
                    <span className="tag tag-green" style={{ fontSize: 10 }}>Concluída</span>
                  </div>
                </div>
                <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--green)' }}>{formatCurrency(goal.targetValue)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div className="row" style={{ marginBottom: 12 }}>
          <span className="section-title" style={{ margin: 0 }}>Orçamento mensal</span>
          <span style={{ fontSize: 11, color: 'var(--muted)' }}>{now.toLocaleString('pt-BR', { month: 'long' })}</span>
        </div>
        {state.budgets.map(budget => {
          const cat = cats[budget.categoryId];
          const spent = budgetSpent[budget.categoryId] || 0;
          const pct = Math.round(spent / budget.planned * 100);
          const color = pct >= 100 ? '#e24b4a' : pct >= budget.alertPct ? 'var(--gold)' : '#1d9e75';
          const msg = pct >= 100 ? 'Orçamento estourado!' : pct >= budget.alertPct ? `${pct}% — atenção` : `${pct}% — dentro do previsto`;
          return (
            <div className="budget-item" key={budget.id} style={{ paddingBottom: 10, marginBottom: 2 }}>
              <div className="row" style={{ marginBottom: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 500 }}>{cat?.name || budget.categoryId}</span>
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>{formatCurrency(spent)} / {formatCurrency(budget.planned)}</span>
              </div>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${Math.min(pct, 100)}%`, background: color }}></div>
              </div>
              <div style={{ fontSize: 11, color, marginTop: 3 }}>{msg}</div>
            </div>
          );
        })}
      </div>

      {modal === 'goal' && <GoalModal goal={editGoal} onClose={() => setModal(null)} />}
      {depositGoal && <DepositModal goal={depositGoal} onClose={() => setDepositGoal(null)} />}
    </div>
  );
}
